import React, { useState, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Provider as StoreProvider } from 'react-redux';
import { StyledEngineProvider, Snackbar, Alert } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import Amplify from 'aws-amplify';
import firebase from 'firebase';
import { store, persistor } from './store';
import routes from './routes';
import './mixins/chartjs';
import './mixins/moment';
import './mixins/validate';
import './assets/scss/index.scss';
import ErrorBoundary from './ErrorBoundary';
import axios from 'axios';
import { PersistGate } from 'redux-persist/integration/react';
import 'leaflet-area-select';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { getLatestJwtToken } from './services/amplify-auth';
import './tailwind.css';
import ScrollReset from './components/ScrollReset/ScrollReset';
import useOnlineStatus from './hooks/useOnlineStatus';
import packageJson from '../package.json';

const deliveryAuthTokenUrl = `https://manna-consumer-app.auth.eu-west-1.amazoncognito.com/oauth2/token`;

let deliveryApiAccessToken;

const getDeliveryApiAuth = () => {
  return new Promise((resolve, reject) => {
    const params = new URLSearchParams();
    params.append('grant_type', 'client_credentials');
    const config = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization:
          'Basic M3U4ZG5sbjNlcThnb2djbWphc2dscmNtMzI6MXY3dXI4czc5cWxuYXFxOW1iMmI4bHNocDdiOG9wMjcxb2Z2M3ZhODhjbzMzN2o1N2I1MA=='
      }
    };

    const refresh = deliveryApiAccessToken?.expiry < Date.now() / 1000;

    if (deliveryApiAccessToken?.access_token && !refresh) {
      resolve(deliveryApiAccessToken?.access_token);
    } else {
      axios
        .post(deliveryAuthTokenUrl, params, config)
        .then(result => {
          deliveryApiAccessToken = {
            ...result.data,
            expiry: Date.now() / 1000 + result.data.expires_in
          };

          resolve(result.data.access_token);
        })
        .catch(err => {
          reject(err);
        });
    }
  });
};

Amplify.configure({
  Auth: {
    region: process.env.REACT_APP_COGNITO_REGION,
    userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
    userPoolWebClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
    mandatorySignIn: false,
    oauth: {
      scope: ['mc-api/read']
    }
  },
  API: {
    endpoints: [
      {
        name: 'api',
        endpoint: process.env.REACT_APP_ADMIN_URL,
        custom_header: async () => {
          return {
            Authorization: `Bearer ${await getLatestJwtToken()}`,
          };
        }
      },
      {
        name: 'orderful-api',
        endpoint: process.env.REACT_APP_ORDERFUL_URL,
        custom_header: async () => {
          return {
            Authorization: `Bearer ${await getLatestJwtToken()}`,
          };
        }
      },
      {
        name: 'delivery',
        endpoint: process.env.REACT_APP_DELIVERY_URL,
        custom_header: async () => {
          const token = await getDeliveryApiAuth();
          return {
            Authorization: `Bearer ${token}`,
            'X-API-Key': 'uolfAvqAcZ1RmkrHfdYmm70aXHKaHem5a36wx47i'
          };
        }
      },
      {
        name: 'flightapi',
        endpoint: process.env.REACT_APP_MANNA_CORE_API_HOST,
        custom_header: async () => {
          return {
            // Authorization : 'token',
            Authorization: `Bearer ${await getLatestJwtToken()}`,
            'X-API-Key': 'kyGhOImQeI5mDYnJ9wKwi97XxtNde2cs7shMUGIT'
          };
        }
      },
      {
        name: 'bom',
        endpoint: 'https://depot.manna.aero',
        custom_header: async () => {
          return {
            Authorization: `Bearer ${await getLatestJwtToken()}`,
            version: 2086,  // static value required by the api
          };
        }
      }
    ]
  }
});

const auth = firebase.auth();

export const signInDeafaultUser = () => {
  return new Promise((resolve, reject) => {
    auth
      .setPersistence(firebase.auth.Auth.Persistence.LOCAL)
      .then(function() {
        auth
          .signInWithEmailAndPassword('brian.smith@manna.aero', 'qwerty12345')
          .then(user => {
            resolve(user);
          });
      })
      .catch(function(error) {
        reject(error);
      });
  });
};

signInDeafaultUser();

const NetworkStatusNotification = () => {
  const connectionStatus = useOnlineStatus();
  
  if (connectionStatus === 'ok') return null;
  
  return (
    <div 
      style={{
        position: 'fixed',
        height: 100,
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: connectionStatus === 'nowifi' ? '#f44336' : '#ff9800',
        color: 'white',
        textAlign: 'center',
        padding: '8px 16px',
        zIndex: 9999,
        fontWeight: 'bold',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        alignContent: 'center'
      }}
    >
      {connectionStatus === 'nowifi' 
        ? 'No network connection. Please check your internet connection.' 
        : 'Connection to server lost. Attempting to reconnect...'}
    </div>
  );
};

const App = () => {
  const [showVersion, setShowVersion] = useState(false);
  const [keySequence, setKeySequence] = useState('');

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if key is 'v'
      if (event.key === 'v') {
        setKeySequence(prev => prev + 'v');
      } else if (event.key === 'Enter' && keySequence === 'vvvvv') {
        setShowVersion(true);
        setKeySequence('');
        // Auto-hide after 3 seconds
        setTimeout(() => {
          setShowVersion(false);
        }, 3000);
      } else {
        // Reset sequence on any other key
        setKeySequence('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [keySequence]);

  return (
    <ErrorBoundary>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DndProvider backend={HTML5Backend}>
          <StoreProvider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <StyledEngineProvider injectFirst>
                <SnackbarProvider maxSnack={4}>
                  <BrowserRouter>
                    <NetworkStatusNotification />
                    <ScrollReset />
                    {routes}
                    <Snackbar
                      open={showVersion}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                      onClose={() => setShowVersion(false)}
                    >
                      <Alert 
                        onClose={() => setShowVersion(false)} 
                        severity="info" 
                        sx={{ width: '100%' }}
                      >
                        Version: {packageJson.version}
                      </Alert>
                    </Snackbar>
                  </BrowserRouter>
                </SnackbarProvider>
              </StyledEngineProvider>
            </PersistGate>
          </StoreProvider>
        </DndProvider>
      </LocalizationProvider>
    </ErrorBoundary>
  );
};

export default App;
